import formatConstructionType from "@/utils/formatConstructionType";
import {
  BuildingTypeResponse,
  MaterialResponse,
  ProjectConstructionType,
  UseTypeEntity,
} from "@/web-client/api";
import { ArchitectureSearchQueryParameter } from "@/types/ArchitectureSearchQueryParameter";
import useSearchCondition, {
  SearchConditionResponse,
} from "@/hooks/useSearchCondition";
import formatNumber from "@/utils/formatNumber";
import Colors from "@/consts/Colors";
import useAllUseTypes from "./useAllUseTypes";
import useFindEntityWithSubEntities from "@/hooks/useFindEntityWithSubEntitites";
import useFindEntityById from "./useFindEntityById";
import { StructureTypes } from "@/config/StrctureTypes";

const useArchitectureSearchCondition = (
  query: ArchitectureSearchQueryParameter,
  buildingTypes: BuildingTypeResponse[],
  materials: MaterialResponse[],
): SearchConditionResponse<ArchitectureSearchQueryParameter> => {
  const {
    selectedEntity: selectedBuildingType,
    parentEntities: buildingTypeParentEntities,
  } = useFindEntityWithSubEntities<BuildingTypeResponse>(
    buildingTypes,
    query.building_type_id,
  );

  const { selectedEntity: selectedMaterial } =
    useFindEntityWithSubEntities<MaterialResponse>(
      materials,
      query.material_id,
    );

  const allUseTypes = useAllUseTypes();
  const selectedUseType = useFindEntityById<UseTypeEntity>(
    allUseTypes,
    query.use_type_id,
  );

  const searchConditions = useSearchCondition<ArchitectureSearchQueryParameter>(
    (conditions) => {
      if (query.keyword) {
        conditions.push({
          key: "keyword",
          label: `キーワード: ${query.keyword}`,
          value: query.keyword,
        });
      }

      if (query.structure_type_id) {
        const structureType = StructureTypes.find(
          (s) => s.id.toString() === query.structure_type_id,
        );
        conditions.push({
          key: "structure_type_id",
          label: `構造: ${structureType?.label}`,
          value: query.structure_type_id,
        });
      }

      if (query.construction_type) {
        conditions.push({
          key: "construction_type",
          label: `工事種別: ${formatConstructionType(
            Number(query.construction_type) as ProjectConstructionType,
          )}`,
          value: query.construction_type,
        });
      }

      if (query.min_floor_space) {
        conditions.push({
          key: "min_floor_space",
          label: `最小面積: ${formatNumber(Number(query.min_floor_space))}㎡`,
          value: query.min_floor_space,
        });
      }

      if (query.max_floor_space) {
        conditions.push({
          key: "max_floor_space",
          label: `最大面積: ${formatNumber(Number(query.max_floor_space))}㎡`,
          value: query.max_floor_space,
        });
      }

      if (selectedBuildingType) {
        conditions.push({
          key: "building_type_id",
          label: `建築の種類: ${selectedBuildingType.name}`,
          value: selectedBuildingType.id.toString(),
        });
      }

      if (selectedMaterial) {
        conditions.push({
          key: "material_id",
          label: `建築材料: ${selectedMaterial.name}`,
          value: selectedMaterial.id.toString(),
        });
      }

      if (query.color_type_ids) {
        const colorIds = query.color_type_ids.split(",");
        colorIds.forEach((v) => {
          const targetColor = Colors.find((color) => color.id.toString() === v);
          conditions.push({
            key: "color_type_ids",
            label: `建築材料の色: ${targetColor.name}`,
            value: v,
          });
        });
      }

      if (selectedUseType) {
        conditions.push({
          key: "use_type_id",
          label: `空間用途: ${selectedUseType.name}`,
          value: String(selectedUseType.id),
        });
      }

      return conditions;
    },
    [
      selectedBuildingType,
      buildingTypeParentEntities,
      selectedMaterial,
      selectedUseType,
      query,
    ],
  );

  return searchConditions;
};

export default useArchitectureSearchCondition;
